import axiosInstance from '@/api/axiosInstance'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AuthContext } from '@/contexts/auth-context'
import { useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import LoyaltyCardEditor from './CardEditor/editor'
import BackgroundForm from './FormComponents/BackgroundForm'
import BarcodeSettingsForm from './FormComponents/BarcodeSettingsForm'
import BusinessNameForm from './FormComponents/BusinessNameForm'
import ContactDetailsForm from './FormComponents/ContactDetailsForm'
import CustomerIdForm from './FormComponents/CustomerIdForm'
import LogoForm from './FormComponents/LogoForm'
import OfferTextForm from './FormComponents/OfferTextForm'
import RewardForm from './FormComponents/RewardForm'
import TaglineForm from './FormComponents/TaglineForm'
import TemplateSelector from './TemplateSelector'
import { getPlanPermissions } from '@/utils/planPermissions'
import logger from '../../utils/logger'

const getImageUrl = (path) => {
  if (
    path === '/card/default-logo.png' ||
    path === '/card/stamp.svg' ||
    path?.includes('/card/temp')
  ) {
    return path
  }
  return `${import.meta.env.VITE_BASE_URL}${path}`
}

// const plan = 'basic' // Change this to "basic", "premium", or "enterprise"

// const permissions = {
//   logo: plan !== 'basic',
//   stampin: {
//     draggable : plan !== 'basic',
//     resizable : plan === "enterprise"
//   },
//   all: plan === 'enterprise',
// }

const CreateCard = () => {
  const { auth } = useContext(AuthContext)
  const plan = auth?.user?.subscription?.planTier || 'basic'
  const permissions = getPlanPermissions(plan)

  const { state } = useLocation()
  const editCard = state?.card || null
  const editCardId = state?.cardId || null
  const isEditMode = !!editCard
  const navigator = useNavigate()
  const [rewardStep, setRewardStep] = useState(1)
  const businessId = auth.user._id
  const [files, setFiles] = useState({
    logo: null,
    backgroundImage: null,
    stampImage: null,
  })

  const [formData, setFormData] = useState({})
  const [activeTab, setActiveTab] = useState(1)
  const [selectedElement, setSelectedElement] = useState(null)
  const [confirmation, setConfirmation] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [changeTemplateConfirm, setChangeTemplateConfirm] = useState(false)
  const [discardConfirm, setDiscardConfirm] = useState(false)

  const setFormDataWithPermissions = (data) => {
    setFormData({
      ...data,

      // setting image URLs for background, logo, and stamp styles
      background: {
        ...data.background,
        value:
          data.background.type === 'image'
            ? getImageUrl(data.background.value)
            : data.background.value,
      },
      logoSettings: {
        ...data.logoSettings,
        image: getImageUrl(data.logoSettings.image),
        draggable: permissions.logo,
        resizable: permissions.logo,
      },
      stampStyles: {
        ...data.stampStyles,
        filled: {
          ...data.stampStyles.filled,
          image: getImageUrl(data.stampStyles.filled.image),
        },
      },
      stampinLogoSettings: {
        ...data.stampinLogoSettings,
        draggable: permissions.stampin.draggable,
        resizable: permissions.stampin.resizable,
      },
      barcodeSettings: {
        ...data.barcodeSettings,
        draggable: permissions.all,
        resizable: permissions.all,
      },
      progressBarStyles: {
        ...data.progressBarStyles,
        draggable: true,
        resizable: true,
      },
      stampContainerStyles: {
        ...data.stampContainerStyles,
        draggable: true,
        resizable: true,
      },

      textElements: {
        businessName: {
          ...data.textElements.businessName,
          draggable: permissions.all,
          resizable: permissions.all,
        },
        tagline: {
          ...data.textElements.tagline,
          draggable: permissions.all,
          resizable: permissions.all,
        },
        contactDetails: {
          ...data.textElements.contactDetails,
          draggable: permissions.all,
          resizable: permissions.all,
        },
        offerText: {
          ...data.textElements.offerText,
          draggable: permissions.all,
          resizable: permissions.all,
        },
        customerId: {
          ...data.textElements.customerId,
          draggable: permissions.all,
          resizable: permissions.all,
        },
        progressText: {
          ...data.textElements.progressText,
          draggable: true,
          resizable: true,
        },
      },
    })
  }

  useEffect(() => {
    if (editCard) {
      // Transform the edit card data to match formData structure
      window.scrollTo({ top: 0, behavior: 'instant' })
      setFormDataWithPermissions(editCard)
      setSelectedTemplate(editCard)
    }
  }, [editCard])

  const tabs = [
    'Background',
    'Name',
    'Logo',
    'Tagline',
    'Contact',
    'Barcode',
    'Offer Desc.',
    'Reward',
  ]

  // Add element to tab mapping
  const elementToTabMap = {
    background: 1,
    businessName: 2,

    logo: 3,
    stampinLogo: 3,

    tagline: 4,
    contactDetails: 5,
    barcode: 6,
    customerId: 6,
    offerText: 7,
    // Adding reward elements
    progressBar: 8,
    stampContainer: 8,
    progressText: 8,
  }

  // Update the element selection handler
  const handleElementSelect = (element) => {
    setSelectedElement(element)

    // // Special handling for reward-related elements
    // if (element === 'progressBar' || element === 'stampContainer' || element === 'progressText') {
    //   setActiveTab(8) // Reward tab
    //   return
    // }
    // if (element === 'stampinLogo') {
    //   setActiveTab(3) // Reward tab
    //   return
    // }

    // Regular tab switching for other elements
    if (element && elementToTabMap[element]) {
      setActiveTab(elementToTabMap[element])
    }
  }

  const handleTextElementChange = (elementKey, property, value) => {
    setFormData((prev) => ({
      ...prev,
      textElements: {
        ...prev.textElements,
        [elementKey]: {
          ...prev.textElements[elementKey],
          [property]: value,
        },
      },
    }))
  }

  const handleElementChange = (elementKey, newProps) => {
    if (elementKey === 'stampContainer') {
      setFormData((prev) => ({
        ...prev,
        stampContainerStyles: {
          ...prev.stampContainerStyles,
          ...newProps,
        },
      }))
    } else if (elementKey === 'progressBar') {
      setFormData((prev) => ({
        ...prev,
        progressBarStyles: {
          ...prev.progressBarStyles,
          ...newProps,
        },
      }))
    } else if (elementKey === 'logo') {
      setFormData((prev) => ({
        ...prev,
        logoSettings: {
          ...prev.logoSettings,
          ...newProps,
        },
      }))
    } else if (elementKey === 'stampinLogo') {
      setFormData((prev) => ({
        ...prev,
        stampinLogoSettings: {
          ...prev.stampinLogoSettings,
          ...newProps,
        },
      }))
    } else if (elementKey === 'barcode') {
      setFormData((prev) => ({
        ...prev,
        barcodeSettings: {
          ...prev.barcodeSettings,
          ...newProps,
        },
      }))
    } else if (newProps.fontSize) {
      // Text elements with fontSize
      const clampedFontSize = Math.min(Math.max(newProps.fontSize, 10), 100)
      setFormData((prev) => ({
        ...prev,
        textElements: {
          ...prev.textElements,
          [elementKey]: {
            ...prev.textElements[elementKey],
            ...newProps,
            fontSize: clampedFontSize,
          },
        },
      }))
    } else {
      // Other text elements updates
      setFormData((prev) => ({
        ...prev,
        textElements: {
          ...prev.textElements,
          [elementKey]: {
            ...prev.textElements[elementKey],
            ...newProps,
          },
        },
      }))
    }
  }

  // Helper function to handle file updates
  const handleFileUpdate = (fileType, file) => {
    // Store the File object
    setFiles((prev) => ({
      ...prev,
      [fileType]: file,
    }))

    // Update previews state with the file type in formdata
    if (file) {
      const preview = URL.createObjectURL(file)
      logger.log('file ======> ', file)
      logger.log('preview ======> ', preview)
      switch (fileType) {
        case 'logo':
          setFormData((prev) => ({
            ...prev,
            logoSettings: {
              ...prev.logoSettings,
              image: preview, // Update the image URL
            },
          }))
          break
        case 'backgroundImage':
          setFormData((prev) => ({
            ...prev,
            background: {
              ...prev.background,
              type: 'image',
              value: preview, // Update the image URL
            },
          }))
          break
        case 'stampImage':
          setFormData((prev) => ({
            ...prev,
            stampStyles: {
              ...prev.stampStyles,
              filled: {
                ...prev.stampStyles.filled,
                image: preview, // Update the stamp image URL
              },
            },
          }))
          break
        default:
          break
      }
    }
  }

  // Modify validateForm to remove image validations in edit mode
  const validateForm = () => {
    // Basic validations
    if (!formData.textElements.businessName.text) {
      toast.error('Business name is required')
      return false
    }

    // Reward validations
    if (!formData.rewardMechanism) {
      toast.error('Please select a reward mechanism')
      return false
    }
    if (!formData.rewardType) {
      toast.error('Please select a reward type')
      return false
    }
    if (!formData.rewardStyle) {
      toast.error('Please select a reward style')
      return false
    }
    if (formData.threshold <= 0) {
      toast.error('Please set a valid threshold')
      return false
    }
    if (formData.rewardQty <= 0) {
      toast.error('Please set a valid reward quantity')
      return false
    }

    if (!isEditMode && formData.rewardStyle === 'stamps' && !files.stampImage) {
      toast.error('Stamp logo image is required')
      return false
    }

    if (formData.logoSettings.image === '/card/default-logo.png') {
      toast.error(
        'This business logo is not allowed. Please upload a custom logo or remove it in logo section !.'
      )
      setActiveTab(elementToTabMap['logo'])
      return false
    }
    return true
  }

  const prepareCardData = () => {
    // removing base URL from image paths which were set in setFormDataWithPermissions for editCard
    const cardData = {
      ...formData,

      background: {
        ...formData.background,
        // value:
        //   formData.background.type === 'image' &&
        //   formData.background.value.includes(import.meta.env.VITE_BASE_URL)
        //     ? formData.background.value.replace(import.meta.env.VITE_BASE_URL, '')
        //     : formData.background.value,
      },

      logoSettings: {
        ...formData.logoSettings,
        image: formData.logoSettings.image.replace(import.meta.env.VITE_BASE_URL, ''),
      },

      stampStyles: {
        ...formData.stampStyles,
        filled: {
          ...formData.stampStyles.filled,
          image: formData.stampStyles.filled.image.replace(import.meta.env.VITE_BASE_URL, ''), // Update the stamp image URL
        },
      },
    }

    // Handle background image value
    if (cardData.background.type === 'image') {
      const backgroundValue = cardData.background.value

      // Check if the value is a VITE_BASE_URL path (for existing images)
      if (
        backgroundValue &&
        typeof backgroundValue === 'string' &&
        backgroundValue.includes(import.meta.env.VITE_BASE_URL)
      ) {
        cardData.background.value = backgroundValue.replace(import.meta.env.VITE_BASE_URL, '')
      }
      // If it's a blob URL or Base64 string (for new uploads), set it to null
      else if (
        backgroundValue &&
        (backgroundValue.startsWith('blob:') || backgroundValue.startsWith('data:'))
      ) {
        cardData.background.value = null
      }
    }

    return cardData
  }

  const handleSave = () => {
    if (!validateForm()) return
    setConfirmation(true)
  }

  const handleConfirmedSave = async () => {
    setIsAdding(true)
    try {
      const formDataToSend = new FormData()

      // Only append files if they have been changed/updated
      if (files.logo) formDataToSend.append('logo', files.logo)
      if (files.backgroundImage && formData.background.type === 'image') {
        formDataToSend.append('backgroundImage', files.backgroundImage)
      }
      if (files.stampImage) {
        formDataToSend.append('stampImage', files.stampImage)
      }

      // Prepare and append card data
      const cardData = prepareCardData()
      logger.log('cardData ====> ', cardData)
      if (isEditMode) {
        formDataToSend.append('cardId', editCardId)
      }

      formDataToSend.append('card', JSON.stringify(cardData))
      formDataToSend.append('businessId', businessId)

      const response = await axiosInstance({
        url: isEditMode ? '/web-api/editCard' : '/web-api/addCard',
        method: 'post',
        data: formDataToSend,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.data.status === 200) {
        toast.success(`Card ${isEditMode ? 'updated' : 'saved'} successfully!`)
        navigator(-1)
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'saving'} card:`, error)
      toast.error(
        error.response?.data?.message || `Error ${isEditMode ? 'updating' : 'saving'} card`
      )
    } finally {
      setIsAdding(false)
      setConfirmation(false)
    }
  }

  const handleTemplateSelect = async (template) => {
    try {
      // Set form data first
      setFormDataWithPermissions(template.formData)

      // setting the selected template background to the state
      if (template.formData.background.type === 'image') {
        try {
          const response = await fetch(template.formData.background.value)
          if (!response.ok) throw new Error('Failed to load background')
          const blob = await response.blob()
          const file = new File([blob], 'background.jpg', { type: 'image/jpeg' })
          handleFileUpdate('backgroundImage', file)
        } catch (error) {
          console.error('Error loading background:', error)
        }
      }

      setSelectedTemplate(template)
      window.scrollTo({ top: 0, behavior: 'instant' })
    } catch (error) {
      console.error('Error loading template:', error)
      toast.error('Failed to load template')
    }
  }

  const handleChangeTemplate = () => {
    setChangeTemplateConfirm(false)
    setSelectedTemplate(null)
    setSelectedElement(null)
    setRewardStep(1)
    handleFileUpdate('logo', null)
    handleFileUpdate('backgroundImage', null)
    handleFileUpdate('stampImage', null)
    setActiveTab(1)
  }

  // Show template selector if no template selected
  if (!selectedTemplate) {
    return <TemplateSelector plan={plan} onSelect={handleTemplateSelect} />
  }

  logger.log('formData =====> ', formData)
  const renderLeftContent = () => {
    switch (activeTab) {
      case 1:
        return (
          <BackgroundForm
            background={formData.background}
            onChange={(background) => setFormData({ ...formData, background })}
            onFileChange={(file) => handleFileUpdate('backgroundImage', file)}
            plan={plan}
          />
        )
      case 2:
        return (
          <BusinessNameForm
            textElement={formData.textElements.businessName}
            onElementChange={handleTextElementChange}
          />
        )
      case 3:
        return (
          <LogoForm
            onFileChange={(file) => handleFileUpdate('logo', file)}
            logoStyle={formData.logoSettings}
            onStyleChange={(styleUpdates) => handleElementChange('logo', styleUpdates)}
            plan={plan}
            stampinLogoSettings={formData.stampinLogoSettings}
            onStampinLogoChange={(styleUpdates) => handleElementChange('stampinLogo', styleUpdates)}
          />
        )
      case 4:
        return (
          <TaglineForm
            textElement={formData.textElements.tagline}
            onElementChange={handleTextElementChange}
          />
        )
      case 5:
        return (
          <ContactDetailsForm
            textElement={formData.textElements.contactDetails}
            onElementChange={handleTextElementChange}
          />
        )
      case 6:
        return (
          <>
            <CustomerIdForm
              textElement={formData.textElements.customerId}
              onElementChange={handleTextElementChange}
            />
            <div className="mt-8">
              <BarcodeSettingsForm
                settings={formData.barcodeSettings}
                onBarcodeSettingChange={(property, value) =>
                  handleElementChange('barcode', { [property]: value })
                }
                plan={plan}
                customerId={formData.textElements.customerId.text}
              />
            </div>
          </>
        )
      case 7:
        return (
          <OfferTextForm
            textElement={formData.textElements.offerText}
            onElementChange={handleTextElementChange}
          />
        )
      case 8:
        return (
          <RewardForm
            formData={formData}
            onUpdate={(newData) => setFormData(newData)}
            plan={plan}
            currentStep={rewardStep}
            onStepChange={setRewardStep}
            onFileChange={handleFileUpdate} // Add this prop
            handleTextElementChange={handleTextElementChange}
          />
        )
      default:
        return <div>Select a tab to edit</div>
    }
  }

  return (
    <div className="flex h-screen">
      <div className="w-[30%] overflow-y-auto border-r p-4 pt-0">{renderLeftContent()}</div>

      <div className="w-[70%] flex flex-col">
        {/* Header - only tabs, no save button */}
        <div className="flex items-center p-4 border-b bg-gray-50">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab, index) => (
              <button
                key={index}
                className={`px-4 py-2 rounded-full transition-all duration-200 text-sm
                  ${
                    activeTab === index + 1
                      ? 'bg-blue-500 text-white shadow-sm'
                      : 'bg-white text-gray-600 border hover:bg-gray-50'
                  }`}
                onClick={() => {
                  setActiveTab(index + 1)
                  setSelectedElement(null)
                }}
              >
                {tab}
              </button>
            ))}

            <button
              onClick={handleSave}
              className="px-3 py-2 bg-green-500 text-white rounded-full hover:bg-green-600 
                       transition-colors flex items-center gap-2 text-sm font-medium shadow-sm"
            >
              <span>Save</span>
            </button>
          </div>
        </div>

        <div className=" flex w-full justify-between items-center ps-1">
          <div
            className={`flex justify-end p-1 cursor-pointer transition-colors text-blue-500 w-fit`}
            onClick={() => setDiscardConfirm(true)}
          >
            <span>Back to Cards</span>
          </div>

          <div
            className={`flex justify-end p-1 cursor-pointer transition-colors text-blue-500 w-fit`}
            onClick={() => setChangeTemplateConfirm(true)}
          >
            <span>Change Template</span>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 p-4">
          <div className="flex justify-center items-center h-full">
            <LoyaltyCardEditor
              logo={formData.logoSettings.image}
              background={formData.background}
              textElements={formData.textElements}
              logoSettings={formData.logoSettings}
              onElementSelect={handleElementSelect} // Updated handler
              selectedElement={selectedElement}
              barcodeSettings={formData.barcodeSettings}
              onElementChange={handleElementChange}
              rewardStyle={formData.rewardStyle}
              progressBarStyles={formData.progressBarStyles}
              stampContainerStyles={formData.stampContainerStyles}
              stampStyles={formData.stampStyles}
              stamps={formData.stamps}
              threshold={formData.threshold}
              stampinLogoSettings={formData?.stampinLogoSettings}
            />
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmation} onOpenChange={setConfirmation}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>
              Have you finalized your card design before saving?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="destructive" onClick={() => setConfirmation(false)}>
              NO, Cancel
            </Button>
            <Button className="bg-blue-600" onClick={handleConfirmedSave} disabled={isAdding}>
              {isAdding ? '...saving' : 'YES, Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Change Confirmation Dialog */}
      <Dialog open={changeTemplateConfirm} onOpenChange={setChangeTemplateConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Template?</DialogTitle>
            <DialogDescription>
              All your current progress will be lost. Are you sure you want to go back and choose a
              different template?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeTemplateConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleChangeTemplate}>
              Change Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Change Confirmation Dialog */}
      <Dialog open={discardConfirm} onOpenChange={setDiscardConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Discarding Changes?</DialogTitle>
            <DialogDescription>
              All your current progress will be lost. Are you sure you want to go back to cards ?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscardConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => navigator('/business/my-cards')}>
              Yes, Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default CreateCard
